/**
 * AnimatedCircle component
 * this component is responsible for rendering the animated circle around the navigation buttons
 * @param radius - the radius of the circle
 * @param steps - the steps of the animation
 * @param duration - the duration of the animation
 * @param buttonColor - the color of the button
 * @returns AnimatedCircle component
 */

import { useState, useEffect } from 'react';

export const AnimatedCircle = ({ radius, steps, duration, buttonColor }: { radius: number; steps: number; duration: number; buttonColor: string }) => {
  // State to store the points of the circle
  const [points, setPoints] = useState<{ x: number; y: number }[]>([]);

  useEffect(() => {
    // Effect to calculate and update points for drawing the circle over time
    const interval = setInterval(() => {
      if (points.length >= steps) {
        clearInterval(interval);
        return;
      }

      const newPoints = [];
      const angleStep = (2 * Math.PI) / steps;

      for (let i = 0; i < points.length + 1; i++) {
        // Calculate the angle for each point, starting from the top
        const angle = i * angleStep - Math.PI / 2;

        // Calculate the coordinates (x, y) for each point on the circle
        const x = radius * Math.cos(angle);
        const y = radius * Math.sin(angle);

        newPoints.push({ x, y });
      }

      // Update the points state with the newly calculated points
      setPoints(newPoints);
    }, duration / steps); // Interval to add points continously, creating the effect of drawing the circle over time

    // Cleanup function
    return () => clearInterval(interval);
  }, [radius, steps, duration, points]);

  // Return the animated circle based on the calculated points
  return (
    <div className='absolute min-[350px]:top-[12px] min-[350px]:left-[12px] md:top-[13px] md:left-[13px]'>
      {points.map((point, index) => (
        <div key={index} className='w-[2px] h-[2px]  rounded-full absolute' style={{ transform: `translate(${point.x}px, ${point.y}px)`, backgroundColor: buttonColor }}></div>
      ))}
    </div>
  );
};
